import { useRef, useEffect, useCallback } from 'react';

/**
 * NexusSoulOrb — animated visualization of the NEXUS soul.
 *
 * States:
 *  - idle:      slow breathing pulse
 *  - listening: reacts to microphone audio level
 *  - thinking:  spinning/processing animation
 *  - speaking:  reacts to playback audio level
 *
 * The orb is drawn on a <canvas> for silky-smooth 60fps animation
 * synchronized with audio amplitude data.
 */

export type OrbState = 'idle' | 'listening' | 'thinking' | 'speaking';

interface NexusSoulOrbProps {
  state: OrbState;
  /** Audio volume 0..1 — feed from AnalyserNode */
  audioLevel?: number;
  size?: number;
}

// Brand colors
const PURPLE = { r: 147, g: 51, b: 234 };   // purple-600
const EMERALD = { r: 16, g: 185, b: 129 };   // emerald-500
const CYAN = { r: 34, g: 211, b: 238 };      // cyan-400

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

export function NexusSoulOrb({ state, audioLevel = 0, size = 200 }: NexusSoulOrbProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const timeRef = useRef(0);
  const smoothLevelRef = useRef(0);

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      const now = performance.now() / 1000;
      const dt = now - (timeRef.current || now);
      timeRef.current = now;

      // Smooth the audio level for organic movement
      const targetLevel = audioLevel;
      const smoothSpeed = state === 'listening' ? 8 : 4;
      smoothLevelRef.current = lerp(smoothLevelRef.current, targetLevel, Math.min(1, dt * smoothSpeed));
      const level = smoothLevelRef.current;

      ctx.clearRect(0, 0, w, h);
      const cx = w / 2;
      const cy = h / 2;
      const baseR = size * 0.22;

      // === Outer glow rings ===
      const ringCount = 4;
      for (let i = ringCount; i >= 0; i--) {
        const phase = now * (0.3 + i * 0.15) + i * 1.2;
        const breathe = Math.sin(phase) * 0.5 + 0.5;
        const audioExpand = state === 'listening' || state === 'speaking' ? level * 0.6 : 0;
        const ringR = baseR + (i + 1) * 12 + breathe * 8 + audioExpand * 30;
        const alpha = (0.08 - i * 0.015) + (state === 'idle' ? breathe * 0.03 : level * 0.06);

        const color =
          state === 'thinking'
            ? `rgba(${CYAN.r}, ${CYAN.g}, ${CYAN.b}, ${alpha})`
            : state === 'speaking'
              ? `rgba(${EMERALD.r}, ${EMERALD.g}, ${EMERALD.b}, ${alpha})`
              : `rgba(${PURPLE.r}, ${PURPLE.g}, ${PURPLE.b}, ${alpha})`;

        ctx.beginPath();
        ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
      }

      // === Main orb with gradient ===
      const orbPulse =
        state === 'idle'
          ? Math.sin(now * 1.2) * 0.06 + 1
          : state === 'thinking'
            ? Math.sin(now * 3) * 0.08 + 1
            : 1 + level * 0.25;
      const orbR = baseR * orbPulse;

      const grad = ctx.createRadialGradient(cx - orbR * 0.25, cy - orbR * 0.25, 0, cx, cy, orbR);
      if (state === 'thinking') {
        const hueShift = (now * 60) % 360;
        grad.addColorStop(0, `hsl(${hueShift}, 80%, 75%)`);
        grad.addColorStop(0.5, `hsl(${hueShift + 40}, 70%, 55%)`);
        grad.addColorStop(1, `hsl(${hueShift + 80}, 60%, 30%)`);
      } else if (state === 'speaking') {
        grad.addColorStop(0, `rgba(${EMERALD.r + 60}, ${EMERALD.g + 40}, ${EMERALD.b + 80}, 1)`);
        grad.addColorStop(0.6, `rgba(${EMERALD.r}, ${EMERALD.g}, ${EMERALD.b}, 0.9)`);
        grad.addColorStop(1, `rgba(${PURPLE.r}, ${PURPLE.g}, ${PURPLE.b}, 0.7)`);
      } else {
        grad.addColorStop(0, `rgba(200, 140, 255, 1)`);
        grad.addColorStop(0.5, `rgba(${PURPLE.r}, ${PURPLE.g}, ${PURPLE.b}, 0.95)`);
        grad.addColorStop(1, `rgba(${EMERALD.r}, ${EMERALD.g}, ${EMERALD.b}, 0.6)`);
      }

      ctx.beginPath();
      ctx.arc(cx, cy, orbR, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();

      // === Inner highlight (specular) ===
      const specGrad = ctx.createRadialGradient(
        cx - orbR * 0.3,
        cy - orbR * 0.3,
        0,
        cx - orbR * 0.1,
        cy - orbR * 0.1,
        orbR * 0.6
      );
      specGrad.addColorStop(0, 'rgba(255, 255, 255, 0.35)');
      specGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.beginPath();
      ctx.arc(cx, cy, orbR, 0, Math.PI * 2);
      ctx.fillStyle = specGrad;
      ctx.fill();

      // === Orbiting particles ===
      const particleCount = state === 'idle' ? 6 : 12;
      for (let i = 0; i < particleCount; i++) {
        const angle = (i / particleCount) * Math.PI * 2 + now * (0.4 + (state === 'thinking' ? 1.5 : 0));
        const dist = orbR + 16 + Math.sin(now * 2 + i) * 8 + level * 20;
        const px = cx + Math.cos(angle) * dist;
        const py = cy + Math.sin(angle) * dist;
        const pr = 1.5 + level * 2 + Math.sin(now * 3 + i * 0.7) * 0.8;

        ctx.beginPath();
        ctx.arc(px, py, pr, 0, Math.PI * 2);
        ctx.fillStyle =
          state === 'thinking'
            ? `rgba(${CYAN.r}, ${CYAN.g}, ${CYAN.b}, ${0.6 + Math.sin(now * 4 + i) * 0.3})`
            : state === 'speaking'
              ? `rgba(${EMERALD.r}, ${EMERALD.g}, ${EMERALD.b}, ${0.5 + level * 0.4})`
              : `rgba(${PURPLE.r}, ${PURPLE.g}, ${PURPLE.b}, ${0.5 + level * 0.4})`;
        ctx.fill();
      }

      // === Waveform ring for listening/speaking ===
      if ((state === 'listening' || state === 'speaking') && level > 0.02) {
        const segments = 64;
        ctx.beginPath();
        for (let i = 0; i <= segments; i++) {
          const angle = (i / segments) * Math.PI * 2;
          const wave = Math.sin(angle * 6 + now * 4) * level * 12 + Math.sin(angle * 3 - now * 2) * level * 6;
          const r = orbR + 8 + wave;
          const x = cx + Math.cos(angle) * r;
          const y = cy + Math.sin(angle) * r;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.strokeStyle =
          state === 'speaking'
            ? `rgba(${EMERALD.r}, ${EMERALD.g}, ${EMERALD.b}, ${0.3 + level * 0.4})`
            : `rgba(${PURPLE.r}, ${PURPLE.g}, ${PURPLE.b}, ${0.3 + level * 0.4})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    },
    [state, audioLevel, size]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const canvasSize = size + 80; // extra room for glow
    canvas.width = canvasSize * dpr;
    canvas.height = canvasSize * dpr;
    canvas.style.width = `${canvasSize}px`;
    canvas.style.height = `${canvasSize}px`;
    ctx.scale(dpr, dpr);

    const loop = () => {
      draw(ctx, canvasSize, canvasSize);
      animRef.current = requestAnimationFrame(loop);
    };
    animRef.current = requestAnimationFrame(loop);

    return () => cancelAnimationFrame(animRef.current);
  }, [draw, size]);

  const canvasSize = size + 80;

  return (
    <canvas
      ref={canvasRef}
      style={{ width: canvasSize, height: canvasSize }}
      className="pointer-events-none"
    />
  );
}
